import { axiosInstance, rawAxios } from '@/services/api/axios';
import type {
  CandidateLoginResponse,
  CandidateRefreshResponse,
  CandidateRegistrationRequest,
  CandidateRegistrationResponse,
} from '@/services/api/types';
import type { AxiosInstance } from 'axios';
import { getDeviceSignature } from '@/utils/deviceSignature';
import { getRecaptchaToken } from '@/utils/recaptcha';

/**
 * The reCAPTCHA action the candidate sign-up mints its token for. It must equal
 * the API route's `verifyRecaptcha('register_candidate')`: siteverify reports
 * the action, and a mismatch is refused like a missing token.
 */
const REGISTER_CANDIDATE_RECAPTCHA_ACTION = 'register_candidate';

interface PasswordResetResponse {
  success?: boolean;
  message: string;
  data?: { userId?: string } | null;
}

export const authApi = {
  /**
   * The API verifies reCAPTCHA on this route unconditionally wherever its
   * RECAPTCHA_ENABLED is on (prod), and refuses a sign-up without a token:
   * "Captcha verification required". The token rides in the BODY, not the
   * `x-recaptcha-token` header, which the public CORS policy does not allow;
   * the API reads either, and its validation strips the field.
   *
   * `verifyBaseUrl` sends this site's origin so the emailed verification link
   * opens this site's /verify-email instead of the EMS app (T100826055). The API
   * honours it only for allow-listed origins; an API without the field strips it.
   */
  register: async (data: CandidateRegistrationRequest): Promise<CandidateRegistrationResponse> => {
    const recaptchaToken = await getRecaptchaToken(REGISTER_CANDIDATE_RECAPTCHA_ACTION);
    const response = await axiosInstance.post<CandidateRegistrationResponse>(
      'auth/register/candidate',
      {
        ...data,
        deviceSignature: getDeviceSignature(),
        verifyBaseUrl: window.location.origin,
        ...(recaptchaToken ? { recaptchaToken } : {}),
      }
    );
    return response.data;
  },

  // Login always authenticates (an unverified email is a 403 error, never a
  // payload variant) — only register returns the CandidateRegistrationData union.
  login: async (email: string, password: string): Promise<CandidateLoginResponse> => {
    const response = await axiosInstance.post<CandidateLoginResponse>('auth/jms/login', {
      email,
      password,
      deviceSignature: getDeviceSignature(),
    });
    return response.data;
  },

  // Refresh returns the rotated tokens FLAT on `data` (see CandidateRefreshData).
  refresh: async (
    data: { refreshToken: string },
    axiosClient: AxiosInstance = rawAxios
  ): Promise<CandidateRefreshResponse> => {
    const response = await axiosClient.post<CandidateRefreshResponse>('auth/refresh', {
      ...data,
      deviceSignature: getDeviceSignature(),
    });
    return response.data;
  },

  // Public (unauthenticated): request a password reset link by email. Sends this
  // app's origin so the BE builds the reset link back to the JMS portal (validated
  // against its allow-list, falling back to FRONTEND_JMS_URL) instead of the EMS app.
  requestPasswordReset: async (email: string): Promise<void> => {
    await axiosInstance.post('auth/password/request-reset', {
      email,
      resetBaseUrl: window.location.origin,
    });
  },

  // Public (unauthenticated): confirm the email with the token from the
  // verification link. No device signature: this only verifies, and the user
  // then signs in on /auth.
  verifyEmail: async (token: string): Promise<void> => {
    await axiosInstance.post('auth/email/verify', { token });
  },

  // Public (unauthenticated): set a new password using the emailed reset token.
  resetPassword: async (
    token: string,
    newPassword: string
  ): Promise<PasswordResetResponse> => {
    const response = await axiosInstance.post<PasswordResetResponse>('auth/password/reset', {
      token,
      newPassword,
    });
    return response.data;
  },
};

